import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";

// ─────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────

type StatusType = "landed" | "airborne" | "scheduled";

interface LocationInfo {
  name: string;
  lat: number;
  lng: number;
}

interface Aircraft {
  callsign: string;
  location: string;
  lat: number;
  lng: number;
  altitude: number;
  speed: number;
  heading: number;
  status: StatusType;
  landedAt: string;
}

interface StatusConfig {
  label: string;
  color: string;
  bg: string;
  icon: string;
}

// ─────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<StatusType, StatusConfig> = {
  airborne: {
    label: "Airborne",
    color: "#f2c14e",
    bg: "rgba(242,193,78,0.14)",
    icon: "✈",
  },
  scheduled: {
    label: "Scheduled",
    color: "#c9a86a",
    bg: "rgba(201,168,106,0.14)",
    icon: "◷",
  },
  landed: {
    label: "Landed",
    color: "#ffdd7a",
    bg: "rgba(255,221,122,0.14)",
    icon: "✈",
  },
};

const STATUSES: StatusType[] = ["landed", "airborne", "scheduled"];

const LOCATIONS: LocationInfo[] = [
  { name: "Mumbai (BOM)", lat: 19.0896, lng: 72.8656 },
  { name: "Delhi (DEL)", lat: 28.5562, lng: 77.1 },
  { name: "Bangalore (BLR)", lat: 13.1986, lng: 77.7066 },
  { name: "Chennai (MAA)", lat: 12.9941, lng: 80.1709 },
  { name: "Hyderabad (HYD)", lat: 17.2403, lng: 78.4294 },
  { name: "Kolkata (CCU)", lat: 22.5726, lng: 88.3639 },
  { name: "Ahmedabad (AMD)", lat: 23.0779, lng: 72.6347 },
  { name: "Pune (PNQ)", lat: 18.5822, lng: 73.9197 },
];

// ─────────────────────────────────────────────────────────
// Leaflet icon factory
// ─────────────────────────────────────────────────────────

const getAircraftIcon = (status: StatusType, heading: number): L.DivIcon => {
  const cfg = STATUS_CONFIG[status];
  const rotation = status === "landed" ? 0 : heading;
  return L.divIcon({
    html: `
      <div class="fr-pin" style="--pin-color:${cfg.color}">
        <div class="fr-pin-ring"></div>
        <div class="fr-pin-glyph" style="transform:rotate(${rotation}deg)">✈</div>
        ${status === "landed" ? '<div class="fr-pin-badge">✓</div>' : ""}
      </div>
    `,
    className: "fr-icon-wrap",
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

// ─────────────────────────────────────────────────────────
// Map controller — flies/zooms to a focused aircraft
// ─────────────────────────────────────────────────────────

const MapFlyTo: React.FC<{ target: Aircraft | null; flyKey: number }> = ({
  target,
  flyKey,
}) => {
  const map = useMap();

  useEffect(() => {
    if (!target) return;
    const zoomLevel = target.status === "landed" ? 12 : 5.5;
    map.flyTo([target.lat, target.lng], zoomLevel, { duration: 1.1 });
  }, [flyKey, map, target]);

  return null;
};

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

const fmtTime = (iso: string): string =>
  new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const randomOf = <T,>(arr: T[]): T =>
  arr[Math.floor(Math.random() * arr.length)];

// ─────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────

const JetAvailability: React.FC = () => {
  const [callsigns, setCallsigns] = useState<string[]>([]);
  const [input, setInput] = useState<string>("");
  const [allData, setAllData] = useState<Aircraft[]>([]);
  const [filtered, setFiltered] = useState<Aircraft[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filterCity, setFilterCity] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [focused, setFocused] = useState<Aircraft | null>(null);
  const [flyKey, setFlyKey] = useState<number>(0);

  const goToAircraft = (a: Aircraft): void => {
    setFocused(a);
    setFlyKey((k) => k + 1);
  };

  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let out = [...allData];
    if (filterCity) {
      out = out.filter((a) =>
        a.location.toLowerCase().includes(filterCity.toLowerCase()),
      );
    }
    if (filterStatus) {
      out = out.filter((a) => a.status === filterStatus);
    }
    setFiltered(out);
  }, [allData, filterCity, filterStatus]);

  useEffect(() => {
    return () => {
      if (errorTimer.current) clearTimeout(errorTimer.current);
    };
  }, []);

  const showError = (msg: string): void => {
    setError(msg);
    if (errorTimer.current) clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setError(""), 3000);
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>): void => {
    const val = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, "")
      .slice(0, 10);
    setInput(val);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") addCallsign();
  };

  const addCallsign = (): void => {
    const v = input.trim();
    if (!v) return;
    if (!/^[A-Z0-9-]{2,10}$/.test(v)) {
      showError("Callsign must be 2–10 alphanumeric characters");
      return;
    }
    if (callsigns.includes(v)) {
      showError("Callsign already added");
      return;
    }
    setCallsigns((prev) => [...prev, v]);
    setInput("");
  };

  const removeCallsign = (cs: string): void => {
    setCallsigns((prev) => prev.filter((c) => c !== cs));
  };

  const clearAll = (): void => {
    setCallsigns([]);
    setAllData([]);
    setFiltered([]);
    setError("");
    setShowFilters(false);
    setFilterCity("");
    setFilterStatus("");
    setInput("");
    setFocused(null);
  };

  const runCheck = async (): Promise<void> => {
    if (callsigns.length === 0) {
      showError("Add at least one callsign");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await new Promise((resolve) => setTimeout(resolve, 700));
      const mock: Aircraft[] = callsigns.map((cs) => {
        const loc = randomOf(LOCATIONS);
        const status = randomOf(STATUSES);
        return {
          callsign: cs,
          location: loc.name,
          lat: loc.lat,
          lng: loc.lng,
          altitude:
            status === "landed" ? 0 : Math.floor(Math.random() * 38000) + 2000,
          speed: status === "landed" ? 0 : Math.floor(Math.random() * 480) + 70,
          heading: Math.floor(Math.random() * 360),
          status,
          landedAt: new Date(
            Date.now() - Math.random() * 86400000,
          ).toISOString(),
        };
      });
      setAllData(mock);
      setShowFilters(true);
      setFilterCity("");
      setFilterStatus("");
      setFocused(mock[0] ?? null);
      setFlyKey((k) => k + 1);
    } catch {
      showError("Error fetching aircraft data");
    } finally {
      setLoading(false);
    }
  };

  const total = filtered.length;
  const airborne = filtered.filter((a) => a.status === "airborne").length;
  const scheduled = filtered.filter((a) => a.status === "scheduled").length;
  const landed = filtered.filter((a) => a.status === "landed").length;

  return (
    <div className="bmj-root">
      <style>{STYLES}</style>

      {/* ── Sidebar ── */}
      <aside className="bmj-sidebar">
        <div className="bmj-sidebar-header">
          <div className="bmj-live-pill">
            <span className="bmj-live-dot" />
            LIVE
          </div>
        </div>

        <div className="bmj-sidebar-body">
          <div>
            <div className="bmj-section-label">Enter callsign</div>
            <div className="bmj-input-wrap">
              <input
                className="bmj-input"
                value={input}
                onChange={handleInput}
                onKeyDown={handleKey}
                placeholder="e.g. VT-ANJ, N12345"
                maxLength={10}
                spellCheck={false}
                autoComplete="off"
              />
              <button
                type="button"
                className="bmj-btn bmj-btn-icon"
                onClick={addCallsign}
                aria-label="Add callsign"
              >
                +
              </button>
            </div>
            <div className="bmj-hint">
              2–10 alphanumeric · press Enter to add
            </div>
          </div>

          {error && <div className="bmj-alert">{error}</div>}

          {callsigns.length > 0 && (
            <div className="bmj-chips">
              {callsigns.map((cs) => (
                <div className="bmj-chip" key={cs}>
                  {cs}
                  <span
                    className="bmj-chip-x"
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${cs}`}
                    onClick={() => removeCallsign(cs)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        removeCallsign(cs);
                    }}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="bmj-actions">
            <button
              type="button"
              className="bmj-btn bmj-btn-primary"
              onClick={runCheck}
              disabled={callsigns.length === 0 || loading}
            >
              {loading ? (
                <span className="bmj-spinner" />
              ) : (
                <span className="bmj-btn-glyph">⌕</span>
              )}
              <span>{loading ? "Checking…" : "Check status"}</span>
            </button>
            <button
              type="button"
              className="bmj-btn bmj-btn-ghost"
              onClick={clearAll}
            >
              ✕ Clear
            </button>
          </div>

          {allData.length > 0 && (
            <div>
              <div className="bmj-section-label">Overview</div>
              <div className="bmj-stats">
                <div className="bmj-stat">
                  <div className="bmj-stat-num" style={{ color: "#1c1c1c" }}>
                    {total}
                  </div>
                  <div className="bmj-stat-lbl">Total</div>
                </div>
                <div className="bmj-stat">
                  <div
                    className="bmj-stat-num"
                    style={{ color: STATUS_CONFIG.airborne.color }}
                  >
                    {airborne}
                  </div>
                  <div className="bmj-stat-lbl">Airborne</div>
                </div>
                <div className="bmj-stat">
                  <div
                    className="bmj-stat-num"
                    style={{ color: STATUS_CONFIG.scheduled.color }}
                  >
                    {scheduled}
                  </div>
                  <div className="bmj-stat-lbl">Scheduled</div>
                </div>
                <div className="bmj-stat">
                  <div
                    className="bmj-stat-num"
                    style={{ color: STATUS_CONFIG.landed.color }}
                  >
                    {landed}
                  </div>
                  <div className="bmj-stat-lbl">Landed</div>
                </div>
              </div>
            </div>
          )}

          {showFilters && (
            <div>
              <div className="bmj-section-label">Filter</div>
              <div className="bmj-filters">
                <input
                  className="bmj-filter-input"
                  placeholder="City / location"
                  value={filterCity}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFilterCity(e.target.value)
                  }
                />
                <select
                  className="bmj-filter-input bmj-filter-select"
                  value={filterStatus}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setFilterStatus(e.target.value)
                  }
                >
                  <option value="">All status</option>
                  <option value="airborne">Airborne</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="landed">Landed</option>
                </select>
              </div>
            </div>
          )}

          <div className="bmj-table-wrap">
            <table className="bmj-table">
              <thead>
                <tr>
                  <th>Callsign</th>
                  <th>Location</th>
                  <th>Altitude / Speed</th>
                  <th>Status</th>
                  <th aria-label="Locate on map"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="bmj-empty">
                        <div className="bmj-empty-icon">✈</div>
                        <div className="bmj-empty-text">
                          {allData.length > 0
                            ? "No aircraft match the current filter"
                            : "Enter a callsign and click Check status"}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((a) => {
                    const cfg = STATUS_CONFIG[a.status];
                    const isFocused = focused?.callsign === a.callsign;
                    return (
                      <tr
                        key={a.callsign}
                        className={isFocused ? "bmj-row-active" : ""}
                        onClick={() => goToAircraft(a)}
                        title={`Locate ${a.callsign} on map`}
                      >
                        <td>
                          <span className="bmj-callsign">{a.callsign}</span>
                        </td>
                        <td>
                          <span className="bmj-location">
                            <span className="bmj-pin-dot" />
                            {a.location}
                          </span>
                        </td>
                        <td className="bmj-numeric">
                          {a.status === "landed"
                            ? `Landed ${fmtTime(a.landedAt)}`
                            : `${a.altitude.toLocaleString()} ft · ${a.speed} kt`}
                        </td>
                        <td>
                          <span
                            className="bmj-badge"
                            style={{ color: cfg.color, background: cfg.bg }}
                          >
                            <span className="bmj-badge-icon">{cfg.icon}</span>
                            {cfg.label}
                          </span>
                        </td>
                        <td className="bmj-goto-cell">
                          <span className="bmj-goto-arrow">→</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </aside>

      {/* ── Main / Map ── */}
      <main className="bmj-main">
        <div className="bmj-main-header">
          <span className="bmj-main-title">Aircraft location</span>
          <span className="bmj-main-count">{filtered.length} aircraft</span>
        </div>

        <div className="bmj-map-area">
          {filtered.length === 0 ? (
            <div className="bmj-map-empty">
              <div className="bmj-map-empty-icon">✈</div>
              <div className="bmj-map-empty-text">
                Run a check to plot aircraft on map
              </div>
            </div>
          ) : (
            <MapContainer
              center={[20.5937, 78.9629]}
              zoom={5}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution="Tiles &copy; Esri"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
              <TileLayer url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />

              <MapFlyTo target={focused} flyKey={flyKey} />

              {filtered.map((a) => (
                <Marker
                  key={a.callsign}
                  position={[a.lat, a.lng]}
                  icon={getAircraftIcon(a.status, a.heading)}
                  eventHandlers={{ click: () => goToAircraft(a) }}
                >
                  <Popup>
                    <div className="bmj-popup">
                      <strong>{a.callsign}</strong>
                      <hr />
                      <div>📍 {a.location}</div>
                      {a.status !== "landed" && (
                        <>
                          <div>
                            ✈ Altitude: {a.altitude.toLocaleString()} ft
                          </div>
                          <div>🚀 Speed: {a.speed} kt</div>
                          <div>🧭 Heading: {a.heading}°</div>
                        </>
                      )}
                      <div>Status: {STATUS_CONFIG[a.status].label}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
      </main>
    </div>
  );
};

export default JetAvailability;

// ─────────────────────────────────────────────────────────
// Styles — fully responsive with inner scroll
// ─────────────────────────────────────────────────────────

const STYLES = `
  html, body, #root {
    height: 100%;
    margin: 0;
    padding: 0;
  }

  * { box-sizing: border-box; }

  .bmj-root {
    display: flex;
    height: 100vh;
    height: 100dvh;
    width: 100%;
    background: #ffffff;
    color: #1c1c1c;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    overflow: hidden;
  }

  /* ─── Sidebar ─── */
  .bmj-sidebar {
    width: 420px;
    min-width: 420px;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-right: 1px solid #e8e3d8;
    overflow: hidden;
    flex-shrink: 0;
  }

  .bmj-sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #ece7da;
    flex-shrink: 0;
  }

  .bmj-live-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 11px;
    border-radius: 100px;
    background: rgba(36,167,114,0.1);
    border: 1px solid rgba(36,167,114,0.3);
    font-size: 10.5px;
    letter-spacing: 1px;
    color: #1f9d6c;
    font-weight: 600;
  }

  .bmj-live-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #1f9d6c;
    box-shadow: 0 0 0 0 rgba(31,157,108,0.5);
    animation: bmj-pulse 1.8s infinite;
  }

  @keyframes bmj-pulse {
    0% { box-shadow: 0 0 0 0 rgba(31,157,108,0.45); }
    70% { box-shadow: 0 0 0 7px rgba(31,157,108,0); }
    100% { box-shadow: 0 0 0 0 rgba(31,157,108,0); }
  }

  .bmj-sidebar-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .bmj-section-label {
    font-size: 11px;
    letter-spacing: 1.3px;
    text-transform: uppercase;
    color: #9a9488;
    margin-bottom: 8px;
    font-weight: 600;
  }

  .bmj-input-wrap { display: flex; gap: 8px; }

  .bmj-input {
    flex: 1;
    background: #faf9f5;
    border: 1px solid #e0dac9;
    border-radius: 9px;
    padding: 11px 13px;
    color: #1c1c1c;
    font-size: 13.5px;
    letter-spacing: 0.6px;
    outline: none;
    transition: border-color 0.15s ease;
  }

  .bmj-input:focus { border-color: #c9a86a; }
  .bmj-input::placeholder { color: #aba596; letter-spacing: 0.2px; }

  .bmj-hint { font-size: 11px; color: #a6a094; margin-top: 6px; }

  .bmj-btn {
    border: none;
    border-radius: 9px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    transition: filter 0.15s ease, opacity 0.15s ease;
  }

  .bmj-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .bmj-btn:not(:disabled):hover { filter: brightness(1.05); }

  .bmj-btn-icon {
    width: 42px;
    background: #faf9f5;
    color: #a3854f;
    border: 1px solid #e0dac9;
    font-size: 18px;
  }

  .bmj-actions { display: flex; gap: 9px; }

  .bmj-btn-primary {
    flex: 1;
    padding: 11px 0;
    background: linear-gradient(135deg, #c9a86a, #a3854f);
    color: #ffffff;
  }

  .bmj-btn-ghost {
    padding: 11px 16px;
    background: transparent;
    border: 1px solid #e0dac9;
    color: #6b6558;
  }

  .bmj-spinner {
    width: 13px;
    height: 13px;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: bmj-spin 0.7s linear infinite;
  }

  @keyframes bmj-spin { to { transform: rotate(360deg); } }

  .bmj-alert {
    background: rgba(214,82,82,0.08);
    border: 1px solid rgba(214,82,82,0.3);
    color: #c23c3c;
    font-size: 12.5px;
    padding: 9px 12px;
    border-radius: 8px;
  }

  .bmj-chips { display: flex; flex-wrap: wrap; gap: 7px; }

  .bmj-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #faf9f5;
    border: 1px solid #e0dac9;
    border-radius: 100px;
    padding: 5px 9px 5px 13px;
    font-size: 12px;
    color: #1c1c1c;
    letter-spacing: 0.4px;
  }

  .bmj-chip-x {
    cursor: pointer;
    color: #a6a094;
    font-size: 15px;
    line-height: 1;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  .bmj-chip-x:hover { background: rgba(214,82,82,0.12); color: #c23c3c; }

  .bmj-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }

  .bmj-stat {
    background: #faf9f5;
    border: 1px solid #ece7da;
    border-radius: 10px;
    padding: 12px 6px;
    text-align: center;
  }

  .bmj-stat-num { font-size: 19px; font-weight: 700; line-height: 1; }
  .bmj-stat-lbl { font-size: 10px; color: #9a9488; margin-top: 5px; letter-spacing: 0.4px; }

  .bmj-filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .bmj-filter-input {
    background: #faf9f5;
    border: 1px solid #e0dac9;
    border-radius: 8px;
    padding: 9px 11px;
    color: #1c1c1c;
    font-size: 12.5px;
    outline: none;
    flex: 1;
  }

  .bmj-filter-input:focus { border-color: #c9a86a; }
  .bmj-filter-select { flex: 0 0 130px; }

  .bmj-table-wrap {
    border: 1px solid #ece7da;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .bmj-table { 
    width: 100%; 
    border-collapse: collapse; 
    font-size: 12.5px;
  }

  .bmj-table thead {
    background: #faf9f5;
  }

  .bmj-table th {
    text-align: left;
    padding: 10px 12px;
    font-size: 10.5px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #9a9488;
    font-weight: 600;
    border-bottom: 1px solid #ece7da;
  }

  .bmj-table td {
    padding: 11px 12px;
    border-bottom: 1px solid #f1ede2;
    color: #4a4640;
  }

  .bmj-table tbody tr { cursor: pointer; transition: background 0.12s ease; }
  .bmj-table tbody tr:hover { background: #fbf8ee; }
  .bmj-table tbody tr:last-child td { border-bottom: none; }

  .bmj-row-active { background: #f6efd9 !important; }

  .bmj-callsign { font-weight: 700; color: #1c1c1c; letter-spacing: 0.5px; }

  .bmj-location { display: flex; align-items: center; gap: 6px; }

  .bmj-pin-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #c9a86a;
    flex-shrink: 0;
  }

  .bmj-numeric { color: #8a8478; font-size: 11.5px; white-space: nowrap; }

  .bmj-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .bmj-badge-icon { font-size: 11px; }

  .bmj-empty { text-align: center; padding: 38px 16px; }
  .bmj-empty-icon { font-size: 28px; opacity: 0.2; margin-bottom: 10px; }
  .bmj-empty-text { font-size: 12.5px; color: #a6a094; }

  .bmj-goto-cell { width: 28px; padding: 11px 10px !important; text-align: center; }

  .bmj-goto-arrow {
    display: inline-flex;
    color: #c9a86a;
    font-size: 14px;
    opacity: 0.5;
    transition: opacity 0.12s ease, transform 0.12s ease;
  }

  .bmj-table tbody tr:hover .bmj-goto-arrow { opacity: 1; transform: translateX(2px); }
  .bmj-row-active .bmj-goto-arrow { opacity: 1; color: #a3854f; }

  /* ─── Main / Map ─── */
  .bmj-main {
    flex: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    position: relative;
  }

  .bmj-main-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 26px;
    border-bottom: 1px solid #ece7da;
    background: #ffffff;
    flex-shrink: 0;
  }

  .bmj-main-title {
    font-size: 13px;
    letter-spacing: 1.3px;
    text-transform: uppercase;
    color: #6b6558;
    font-weight: 600;
  }

  .bmj-main-count {
    font-size: 12px;
    color: #a3854f;
    background: rgba(201,168,106,0.12);
    padding: 4px 11px;
    border-radius: 100px;
  }

  .bmj-map-area { flex: 1; min-height: 0; position: relative; background: #f3f1ea; }

  .bmj-map-empty {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .bmj-map-empty-icon { font-size: 46px; opacity: 0.15; color: #a3854f; }
  .bmj-map-empty-text { font-size: 13px; color: #a6a094; }

  /* ─── Leaflet overrides ─── */
  .fr-icon-wrap { background: transparent !important; border: none !important; }

  .fr-pin {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .fr-pin-ring {
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    background: var(--pin-color);
    opacity: 0.22;
  }

  .fr-pin-glyph {
    position: relative;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--pin-color);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.22);
  }

  .fr-pin-badge {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #1f9d6c;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    font-weight: 700;
    border: 1.5px solid #ffffff;
  }

  .bmj-btn-glyph { font-size: 15px; line-height: 1; }

  .bmj-popup { font-size: 12.5px; line-height: 1.5; color: #1a1a1a; }
  .bmj-popup hr { border: none; border-top: 1px solid #ddd; margin: 6px 0; }

  /* ─── RESPONSIVE ─── */
  @media (max-width: 1024px) {
    .bmj-sidebar {
      width: 380px;
      min-width: 380px;
    }
  }

  @media (max-width: 900px) {
    .bmj-sidebar {
      width: 340px;
      min-width: 340px;
    }
  }

  @media (max-width: 768px) {
    .bmj-root {
      flex-direction: column;
      height: 100vh;
      height: 100dvh;
      overflow: hidden;
    }

    .bmj-sidebar {
      width: 100% !important;
      min-width: 0 !important;
      height: 50%;
      border-right: none;
      border-bottom: 1px solid #e8e3d8;
      flex-shrink: 0;
    }

    .bmj-main {
      height: 50%;
      flex: 1;
    }

    .bmj-sidebar-body {
      padding: 14px 16px 20px;
      gap: 12px;
    }

    .bmj-sidebar-header {
      padding: 14px 16px 12px;
    }

    .bmj-stats {
      grid-template-columns: repeat(4, 1fr);
      gap: 5px;
    }

    .bmj-stat {
      padding: 8px 4px;
    }

    .bmj-stat-num {
      font-size: 16px;
    }

    .bmj-table th,
    .bmj-table td {
      padding: 8px 8px;
      font-size: 11px;
    }

    .bmj-badge {
      padding: 2px 8px;
      font-size: 10px;
    }

    .bmj-filters {
      flex-wrap: wrap;
    }

    .bmj-filter-input {
      flex: 1 1 100%;
    }

    .bmj-filter-select {
      flex: 1 1 100%;
    }

    .bmj-main-header {
      padding: 10px 16px;
    }

    .bmj-main-title {
      font-size: 11px;
    }

    .bmj-main-count {
      font-size: 10px;
      padding: 2px 10px;
    }

    .bmj-btn-primary {
      padding: 10px 0;
      font-size: 12px;
    }

    .bmj-btn-ghost {
      padding: 10px 14px;
      font-size: 12px;
    }

    .bmj-input {
      padding: 9px 11px;
      font-size: 12px;
    }

    .bmj-chip {
      padding: 4px 8px 4px 10px;
      font-size: 11px;
    }

    .fr-pin {
      width: 32px;
      height: 32px;
    }

    .fr-pin-glyph {
      width: 20px;
      height: 20px;
      font-size: 10px;
    }

    .bmj-popup {
      font-size: 11px;
    }

    .bmj-table-wrap {
      border-radius: 8px;
    }

    /* Ensure table scrolls on mobile */
    .bmj-table-wrap {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .bmj-table {
      font-size: 11px;
      min-width: 500px;
    }
  }

  @media (max-width: 480px) {
    .bmj-sidebar {
      height: 45%;
    }

    .bmj-main {
      height: 55%;
    }

    .bmj-sidebar-body {
      padding: 10px 12px 16px;
      gap: 10px;
    }

    .bmj-sidebar-header {
      padding: 10px 12px 10px;
    }

    .bmj-stats {
      grid-template-columns: repeat(4, 1fr);
      gap: 4px;
    }

    .bmj-stat {
      padding: 6px 2px;
      border-radius: 8px;
    }

    .bmj-stat-num {
      font-size: 14px;
    }

    .bmj-stat-lbl {
      font-size: 8px;
      margin-top: 3px;
    }

    .bmj-table th,
    .bmj-table td {
      padding: 6px 5px;
      font-size: 10px;
    }

    .bmj-callsign {
      font-size: 10px;
    }

    .bmj-badge {
      font-size: 9px;
      padding: 2px 6px;
      gap: 3px;
    }

    .bmj-badge-icon {
      font-size: 9px;
    }

    .bmj-input-wrap {
      flex-wrap: wrap;
    }

    .bmj-btn-icon {
      width: 36px;
      font-size: 16px;
    }

    .bmj-actions {
      flex-wrap: wrap;
    }

    .bmj-btn-primary {
      flex: 1 1 100%;
      padding: 10px 0;
    }

    .bmj-btn-ghost {
      flex: 1 1 100%;
      padding: 10px 0;
    }

    .bmj-main-header {
      padding: 6px 12px;
    }

    .bmj-main-title {
      font-size: 10px;
      letter-spacing: 0.8px;
    }

    .bmj-main-count {
      font-size: 9px;
      padding: 2px 8px;
    }

    .bmj-map-empty-icon {
      font-size: 32px;
    }

    .bmj-map-empty-text {
      font-size: 11px;
    }

    .bmj-filter-input {
      font-size: 11px;
      padding: 7px 9px;
    }

    .bmj-section-label {
      font-size: 10px;
      margin-bottom: 5px;
    }

    .bmj-table {
      min-width: 400px;
    }
  }

  /* Extra small devices */
  @media (max-width: 360px) {
    .bmj-sidebar {
      height: 40%;
    }

    .bmj-main {
      height: 60%;
    }

    .bmj-table {
      min-width: 350px;
      font-size: 9px;
    }

    .bmj-table th,
    .bmj-table td {
      padding: 4px 4px;
      font-size: 9px;
    }

    .bmj-badge {
      font-size: 8px;
      padding: 1px 4px;
    }
  }
`;
